import { useEffect, useMemo, useState } from 'react'
import { ChevronRight, X } from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { Input } from '@/core/ui/Input'
import { cn } from '@/lib/cn'
import type { CatServico } from '@/core/db/entities'
import { PickerSheet } from '@/features/common/PickerSheet'
import {
  useDiametrosPorProjeto,
  useMateriaisRedePorProjeto,
  useTiposRedePorProjeto,
  useTrechosPorProjeto,
} from '@/core/db/daos'
import { newApontamentoDraft, type ApontamentoDraft } from './producaoStore'

/**
 * Bottom sheet com formulário dinâmico dirigido pelas flags do serviço.
 * Reescrito 2026-05-27 pra bater 1:1 com a ficha "Controle de produção" do SIGA
 * (pnj_controle_producao). Mudanças desta versão:
 *
 *  - **Material**: read-only, vem de `servico.materialId/materialNome` (do cadastro).
 *    Operador NÃO escolhe. Quando o serviço tem material vinculado, qtd material
 *    fica obrigatória (regra do EGP).
 *  - **Unidade do serviço** e **Unidade do material**: linhas read-only separadas
 *    (paridade visual com SIGA), em vez de aparecerem dentro do label.
 *  - **Material rede / DN / Tipo rede**: pickers escopados por projeto via
 *    cdt_projeto_tem_material_rede / cdt_projeto_tem_diametro / cdt_projeto_tem_tipo_rede.
 *    Operador escolhe da lista cadastrada — sem entrada livre.
 *  - **Trecho**: já era escopado por projeto. Sempre obrigatório.
 *  - **Identificador**: REMOVIDO. Não existe coluna correspondente em
 *    pnj_controle_producao — o dado se perdia.
 *
 * Validação flag-driven: cada flag ON ⇒ campo aparece E vira obrigatório.
 * Botão "Adicionar/Atualizar" desabilitado enquanto faltar campo obrigatório,
 * com mensagem inline listando o que falta.
 */

interface Props {
  servico: CatServico
  projetoId: number | null
  initial: ApontamentoDraft | null
  onSave: (draft: ApontamentoDraft) => void
  onDismiss: () => void
}

type PickerKind = 'materialRede' | 'diametro' | 'tpRede' | 'trecho' | null

export function ServicoFormSheet({ servico, projetoId, initial, onSave, onDismiss }: Props) {
  const isEdit = initial != null

  // Material default vem do serviço (read-only). Se editando draft antigo que
  // tem material diferente, prevalece o do draft pra não perder dado.
  const materialId = initial?.materialId ?? servico.materialId
  const materialNome = initial?.materialNome ?? servico.materialNome
  const materialUnidadeId = initial?.materialUnidadeId ?? servico.materialUnidadeId
  const materialUnidadeNome = initial?.materialUnidadeNome ?? servico.materialUnidadeNome
  const temMaterial = materialId != null

  // Estado local — string para inputs numéricos (vírgula/ponto), refletido no save
  const [qtd, setQtd] = useState(initial?.qtd?.toString() ?? '')
  const [materialQtd, setMaterialQtd] = useState(initial?.materialQtd?.toString() ?? '')
  const [alturaMedia, setAlturaMedia] = useState(initial?.alturaMedia?.toString() ?? '')
  const [materialRedeId, setMaterialRedeId] = useState<number | null>(
    initial?.materialRedeId ?? null,
  )
  const [materialRedeNome, setMaterialRedeNome] = useState<string | null>(
    initial?.materialRedeNome ?? null,
  )
  const [diametroId, setDiametroId] = useState<number | null>(initial?.diametroId ?? null)
  const [diametroNome, setDiametroNome] = useState<string | null>(initial?.diametroNome ?? null)
  const [tpRedeId, setTpRedeId] = useState<number | null>(initial?.tpRedeId ?? null)
  const [tpRedeNome, setTpRedeNome] = useState<string | null>(initial?.tpRedeNome ?? null)
  const [trechoId, setTrechoId] = useState<number | null>(initial?.trechoId ?? null)
  const [trechoNome, setTrechoNome] = useState<string | null>(initial?.trechoNome ?? null)
  // Estaca: 2 campos (inicial + final). Salvos separados nas colunas
  // `controle_producao_estaca_inicial`/`_final` E concatenados como
  // "inicial - final" na coluna legacy `controle_producao_estaca` (compat
  // com SIGA web atual que ainda lê dessa coluna).
  //
  // Hydration: prefere os campos separados do draft (entries novas);
  // fallback pra split da concatenação `estaca` (entries antigas).
  const [estacaInicial, setEstacaInicial] = useState(() => {
    if (initial?.estacaInicial != null) return initial.estacaInicial
    const e = initial?.estaca ?? ''
    const idx = e.indexOf(' - ')
    return idx > 0 ? e.slice(0, idx) : e
  })
  const [estacaFinal, setEstacaFinal] = useState(() => {
    if (initial?.estacaFinal != null) return initial.estacaFinal
    const e = initial?.estaca ?? ''
    const idx = e.indexOf(' - ')
    return idx > 0 ? e.slice(idx + 3) : ''
  })
  const [obs, setObs] = useState(initial?.obs ?? '')

  const [openPicker, setOpenPicker] = useState<PickerKind>(null)

  // Todos os pickers escopados por projeto via cdt_projeto_tem_*.
  const materiaisRede = useMateriaisRedePorProjeto(projetoId)
  const diametros = useDiametrosPorProjeto(projetoId)
  const tiposRede = useTiposRedePorProjeto(projetoId)
  const trechos = useTrechosPorProjeto(projetoId)

  // Escape fecha
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onDismiss])

  // Lista de campos pendentes — alimenta o botão (disabled) e a mensagem inline.
  // Reflete as flags do serviço; trecho é sempre obrigatório; qtd material vira
  // obrigatória quando há material vinculado (regra acordada com o EGP).
  const camposPendentes = useMemo(() => {
    const out: string[] = []
    if (servico.flagQtdExecutada && parseNum(qtd) == null) out.push('Quantidade executada')
    if ((servico.flagQtdMaterial || temMaterial) && parseNum(materialQtd) == null) {
      out.push('Quantidade de material')
    }
    if (servico.flagAlturaMedia && parseNum(alturaMedia) == null) out.push('Altura média')
    if (servico.flagMaterialRede && materialRedeId == null) out.push('Material rede')
    if (servico.flagDn && diametroId == null) out.push('DN')
    if (servico.flagTpRede && tpRedeId == null) out.push('Tipo rede')
    if (trechoId == null) out.push('Trecho')
    return out
  }, [
    servico,
    temMaterial,
    qtd,
    materialQtd,
    alturaMedia,
    materialRedeId,
    diametroId,
    tpRedeId,
    trechoId,
  ])

  const podeAdicionar = camposPendentes.length === 0

  function handleSave() {
    if (!podeAdicionar) return
    const base = initial ?? newApontamentoDraft(servico)
    onSave({
      ...base,
      servico,
      qtd: parseNum(qtd),
      // Material: read-only do serviço, propagado sem mudança
      materialId,
      materialNome,
      materialQtd: parseNum(materialQtd),
      materialUnidadeId,
      materialUnidadeNome,
      alturaMedia: parseNum(alturaMedia),
      materialRedeId,
      materialRedeNome,
      diametroId,
      diametroNome,
      tpRedeId,
      tpRedeNome,
      trechoId,
      trechoNome,
      estaca: joinEstaca(estacaInicial, estacaFinal),
      estacaInicial: estacaInicial.trim() || null,
      estacaFinal: estacaFinal.trim() || null,
      obs: obs.trim() || null,
    })
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center bg-black/60 backdrop-blur-sm"
      onClick={onDismiss}
    >
      <div
        className="card-surface w-full sm:max-w-md max-h-[90vh] rounded-t-2xl sm:rounded-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="flex items-center justify-center pt-3 pb-1 sm:hidden">
          <div className="h-1 w-12 rounded-full bg-on-surface-muted" />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between px-4 pt-4 pb-3 border-b border-border">
          <div className="flex-1 min-w-0 pr-2">
            <p className="text-xs font-bold text-accent uppercase">
              SERVIÇO · {servico.tp}
              {servico.formulario ? ` · ${servico.formulario}` : ''}
            </p>
            <h2 className="text-lg font-bold text-on-surface mt-1 truncate">{servico.nome}</h2>
            <p className="text-xs text-on-surface-dim mt-0.5">
              {activeFlagsSummary(servico)}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onDismiss} aria-label="Fechar">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Form — ordem espelha colunas de pnj_controle_producao */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {/* 1. Quantidade executada (flag-driven) */}
          {servico.flagQtdExecutada && (
            <NumericField
              label="Quantidade executada *"
              value={qtd}
              onChange={setQtd}
            />
          )}

          {/* 2. Unidade do serviço — read-only */}
          <ReadOnlyRow label="UNIDADE" value={servico.unidadeNome ?? '—'} />

          {/* 3. Material — read-only, vem do cadastro do serviço */}
          {temMaterial && <ReadOnlyRow label="MATERIAL" value={materialNome ?? '—'} />}

          {/* 4. Qtd. material — obrigatória se há material vinculado OU flag ON */}
          {(servico.flagQtdMaterial || temMaterial) && (
            <NumericField
              label="Quantidade de material *"
              value={materialQtd}
              onChange={setMaterialQtd}
            />
          )}

          {/* 5. Unidade do material — read-only */}
          {temMaterial && (
            <ReadOnlyRow label="UNIDADE MATERIAL" value={materialUnidadeNome ?? '—'} />
          )}

          {/* 6. Altura média */}
          {servico.flagAlturaMedia && (
            <NumericField
              label="Altura média (m) *"
              value={alturaMedia}
              onChange={setAlturaMedia}
            />
          )}

          {/* 7. Material rede (picker, por projeto) */}
          {servico.flagMaterialRede && (
            <PickerField
              label="MATERIAL REDE *"
              value={materialRedeNome ?? 'Selecionar…'}
              onClick={() => setOpenPicker('materialRede')}
              empty={materialRedeId == null}
              hint={
                (materiaisRede?.length ?? 0) === 0
                  ? 'Sem materiais de rede vinculados a este projeto'
                  : undefined
              }
            />
          )}

          {/* 8. DN — picker da lista do projeto (cdt_projeto_tem_diametro) */}
          {servico.flagDn && (
            <PickerField
              label="DN *"
              value={diametroNome ?? 'Selecionar…'}
              onClick={() => setOpenPicker('diametro')}
              empty={diametroId == null}
              hint={
                (diametros?.length ?? 0) === 0
                  ? 'Sem diâmetros vinculados a este projeto'
                  : undefined
              }
            />
          )}

          {/* 9. Tipo rede (picker, por projeto) */}
          {servico.flagTpRede && (
            <PickerField
              label="TIPO REDE *"
              value={tpRedeNome ?? 'Selecionar…'}
              onClick={() => setOpenPicker('tpRede')}
              empty={tpRedeId == null}
              hint={
                (tiposRede?.length ?? 0) === 0
                  ? 'Sem tipos de rede vinculados a este projeto'
                  : undefined
              }
            />
          )}

          {/* 10. Trecho — sempre obrigatório */}
          <PickerField
            label="TRECHO *"
            value={trechoNome ?? 'Selecionar…'}
            onClick={() => setOpenPicker('trecho')}
            empty={trechoId == null}
            hint={
              (trechos?.length ?? 0) === 0
                ? 'Sem trechos cadastrados para este projeto'
                : undefined
            }
          />

          {/* 11. Estaca inicial + final — texto livre, opcional.
              Salvas concatenadas como "inicial - final" no SIGA. */}
          <div className="grid grid-cols-2 gap-2">
            <LabeledInput
              label="Estaca inicial"
              value={estacaInicial}
              onChange={setEstacaInicial}
              placeholder="193+800"
            />
            <LabeledInput
              label="Estaca final"
              value={estacaFinal}
              onChange={setEstacaFinal}
              placeholder="194+500"
            />
          </div>

          {/* 12. Observações — opcional */}
          <div>
            <label className="text-xs font-medium text-on-surface-dim uppercase">
              Observações
            </label>
            <textarea
              value={obs}
              onChange={(e) => setObs(e.target.value)}
              rows={3}
              className={cn(
                'mt-1 w-full rounded-lg border border-border bg-surface-1 px-3 py-2',
                'text-on-surface placeholder:text-on-surface-muted',
                'focus-visible:border-accent focus-visible:outline-none',
              )}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="border-t border-border px-4 py-3 space-y-2">
          {!podeAdicionar && (
            <p className="text-xs text-warn text-center">
              Preencha: {camposPendentes.join(', ')}
            </p>
          )}
          <div className="flex gap-3">
            <Button variant="outline" size="lg" onClick={onDismiss} className="flex-1">
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={handleSave}
              disabled={!podeAdicionar}
              className="flex-1"
            >
              {isEdit ? 'Atualizar' : 'Adicionar'}
            </Button>
          </div>
        </div>
      </div>

      {/* Pickers aninhados */}
      {openPicker === 'materialRede' && (
        <PickerSheet
          open
          title="Material da rede"
          items={materiaisRede ?? []}
          label={(m) => m.nome}
          onPick={(m) => {
            setMaterialRedeId(m.id)
            setMaterialRedeNome(m.nome)
            setOpenPicker(null)
          }}
          onDismiss={() => setOpenPicker(null)}
          emptyMessage="Sem materiais de rede vinculados a este projeto."
        />
      )}
      {openPicker === 'diametro' && (
        <PickerSheet
          open
          title="Diâmetro (DN)"
          items={diametros ?? []}
          label={(d) => d.nome}
          onPick={(d) => {
            setDiametroId(d.id)
            setDiametroNome(d.nome)
            setOpenPicker(null)
          }}
          onDismiss={() => setOpenPicker(null)}
          emptyMessage="Sem diâmetros vinculados a este projeto."
        />
      )}
      {openPicker === 'tpRede' && (
        <PickerSheet
          open
          title="Tipo de rede"
          items={tiposRede ?? []}
          label={(t) => t.nome}
          onPick={(t) => {
            setTpRedeId(t.id)
            setTpRedeNome(t.nome)
            setOpenPicker(null)
          }}
          onDismiss={() => setOpenPicker(null)}
          emptyMessage="Sem tipos de rede vinculados a este projeto."
        />
      )}
      {openPicker === 'trecho' && (
        <PickerSheet
          open
          title="Trecho"
          items={trechos ?? []}
          label={(t) => t.nome}
          onPick={(t) => {
            setTrechoId(t.id)
            setTrechoNome(t.nome)
            setOpenPicker(null)
          }}
          onDismiss={() => setOpenPicker(null)}
          emptyMessage="Sem trechos para este projeto."
        />
      )}
    </div>
  )
}

// ----------------------------------------------------------------------------
//  Sub-componentes
// ----------------------------------------------------------------------------

/**
 * Input numérico com máscara brasileira:
 *  - Operador digita só dígitos ("123456") → display vira "123.456"
 *  - Digita vírgula → vira separador decimal ("1234,5" → "1.234,5")
 *  - Valor armazenado externamente continua em formato canônico com ponto
 *    decimal ("1234.5") — `parseNum()` da validação não precisa mudar.
 *  - Aceita até 4 casas decimais (qtd material pode ser fracionário).
 */
function NumericField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (s: string) => void
}) {
  const display = value === '' ? '' : formatNumberBR(value.replace('.', ','))
  return (
    <div>
      <label className="text-xs font-medium text-on-surface-dim uppercase">{label}</label>
      <Input
        type="text"
        inputMode="decimal"
        value={display}
        onChange={(e) => {
          const formatted = formatNumberBR(e.target.value)
          onChange(parseNumberBR(formatted))
        }}
        className="mt-1"
      />
    </div>
  )
}

/**
 * "1234,56" / "1.234,56" / "1234.56" → "1.234,56". Tolerante a entrada com
 * pontos e vírgulas misturados (operador colando texto). Mantém apenas a
 * PRIMEIRA vírgula como decimal; o resto vira separador de milhar.
 */
function formatNumberBR(raw: string): string {
  // Substitui pontos por nada (assume que vieram como milhar) e tudo não
  // dígito-ou-vírgula vira nada.
  let s = raw.replace(/\./g, '').replace(/[^\d,]/g, '')
  const firstComma = s.indexOf(',')
  if (firstComma !== -1) {
    // Mantém só a primeira vírgula (decimal); descarta demais.
    s = s.slice(0, firstComma + 1) + s.slice(firstComma + 1).replace(/,/g, '')
  }
  const [intPart = '', decPart] = s.split(',')
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  if (decPart === undefined) return intFormatted
  // Limita a 4 casas decimais visíveis
  return `${intFormatted},${decPart.slice(0, 4)}`
}

/** "1.234,56" → "1234.56" (formato canônico pra parseNum). */
function parseNumberBR(display: string): string {
  return display.replace(/\./g, '').replace(',', '.')
}

/**
 * Junta as duas estacas pro formato que vai pro SIGA. Comportamento:
 *  - Ambas preenchidas: "193+800 - 194+500"
 *  - Só inicial preenchida: "193+800"
 *  - Só final preenchida: "194+500"
 *  - Nenhuma: null
 */
function joinEstaca(inicial: string, final: string): string | null {
  const i = inicial.trim()
  const f = final.trim()
  if (i && f) return `${i} - ${f}`
  if (i) return i
  if (f) return f
  return null
}

function LabeledInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string
  value: string
  onChange: (s: string) => void
  placeholder?: string
}) {
  return (
    <div>
      <label className="text-xs font-medium text-on-surface-dim uppercase">{label}</label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1"
      />
    </div>
  )
}

/**
 * Linha read-only com label uppercase + valor. Usada pra unidade do serviço,
 * material vinculado e unidade do material — campos que o operador não escolhe,
 * vêm do cadastro.
 */
function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="card-surface-variant px-3 py-2">
      <p className="text-xs font-medium text-on-surface-dim uppercase">{label}</p>
      <p className="text-on-surface mt-0.5">{value}</p>
    </div>
  )
}

function PickerField({
  label,
  value,
  onClick,
  empty,
  hint,
}: {
  label: string
  value: string
  onClick: () => void
  empty?: boolean
  hint?: string
}) {
  return (
    <div>
      <label className="text-xs font-medium text-on-surface-dim uppercase">{label}</label>
      <div className="mt-1">
        <button
          type="button"
          onClick={onClick}
          className={cn(
            'w-full text-left rounded-lg border bg-surface-1 px-3',
            'flex items-center justify-between min-h-touch',
            'transition-colors',
            empty
              ? 'border-warn/60 hover:border-warn'
              : 'border-border hover:border-accent/60',
          )}
        >
          <span className={cn('truncate', empty ? 'text-on-surface-dim' : 'text-on-surface')}>
            {value}
          </span>
          <ChevronRight className="h-4 w-4 text-on-surface-dim flex-shrink-0" />
        </button>
      </div>
      {hint && <p className="text-xs text-on-surface-dim mt-1">{hint}</p>}
    </div>
  )
}

function parseNum(s: string): number | null {
  if (s.trim() === '') return null
  const n = Number(s.replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

function activeFlagsSummary(s: CatServico): string {
  const flags: string[] = []
  if (s.flagQtdExecutada) flags.push('qtd executada')
  if (s.flagQtdMaterial) flags.push('qtd material')
  if (s.flagAlturaMedia) flags.push('h média')
  if (s.flagMaterialRede) flags.push('mat. rede')
  if (s.flagDn) flags.push('DN')
  if (s.flagTpRede) flags.push('tp. rede')
  return flags.length === 0 ? 'sem campos obrigatórios extras' : flags.join(' · ')
}
